"use client";

import { Input as InputPrimitive } from "@base-ui/react/input";
import type * as React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type InputProps = Omit<
  InputPrimitive.Props & React.RefAttributes<HTMLInputElement>,
  "size"
> & Omittable<{
  size?: "xs" | "sm" | "default" | "lg" | "xl" | "2xl" | number;
  /** Chrome treatment. `outline`: page fill in both modes (no dark tint),
   *  no shadow — the border is the control, brightening on hover; the
   *  themed border stays reserved for focus. For form fields sitting on a
   *  `background`-filled surface. `ghost`: the hairline with no fill, for a
   *  field resting on a painted surface. `table`: no chrome at all — the cell
   *  is the control's edge, and focus paints on the cell, not the field. */
  variant?: "outline" | "ghost" | "table";
  /**
   * Raw background color for a non-themed canvas surface. Use this when the
   * canvas owns its palette, such as Google Maps. The control keeps its normal
   * border, focus, validation, and disabled treatments, and floats on the
   * popover surface shadow — the same depth every `ControlBar
   * backgroundColor` bar carries over the same canvas.
   */
  backgroundColor?: string;
  /**
   * Raw resting border color, paired with `backgroundColor`, for a canvas
   * whose ground the theme cannot know: the caller resolves a line legible
   * against both the control and the canvas. Only the RESTING border moves —
   * it recolors the `--input` variable locally, so focus, hover, and
   * validation keep their themed treatments.
   */
  borderColor?: string;
  unstyled?: boolean;
  nativeInput?: boolean;
  /** Render only the input element: no shell span, no fixed height,
   * line-height, or padding — beyond dropping the UA focus outline (the
   * host draws the focus chrome), the className passed in is ALL the
   * styling. For hosts that own the control's metrics themselves (inline
   * edits, list search headers), where even `unstyled` keeps the inner
   * input's own height and would fight the host's centering. */
  bare?: boolean;
}>;

/**
 * A control inside a table cell: the cell draws the edge and the focus ring,
 * so the control drops its own border, fill, shadow, radius and ring. Both
 * ring spellings are needed — Input rings from its shell (`has-focus-visible:`),
 * Select from the trigger itself (`focus-visible:`).
 */
export const TABLE_CONTROL_CLASS =
  "rounded-none border-0 bg-transparent shadow-none before:hidden ring-0 focus-visible:ring-0 has-focus-visible:ring-0 dark:bg-transparent [&_input]:px-0";

export function Input({
  className,
  size = "default",
  variant,
  backgroundColor,
  borderColor,
  unstyled = false,
  nativeInput = false,
  bare = false,
  style,
  ...props
}: InputProps): React.ReactElement {
  if (bare) {
    return (
      <InputPrimitive
        // The host owns focus presentation too (a ringed edit row, a bordered
        // cell) — the UA focus outline on the raw input would draw a second
        // ring inside it. Every other class is the caller's.
        className={cn("select-text outline-none", className)}
        data-slot="input"
        style={style}
        {...props}
      />
    );
  }
  const inputClassName = cn(
    "h-8.5 w-full min-w-0 rounded-[inherit] px-[calc(--spacing(3)-1px)] leading-8.5 outline-none [transition:background-color_5000000s_ease-in-out_0s] placeholder:text-muted-foreground/72 sm:h-7.5 sm:leading-7.5",
    // Pairs with Button size="xs"/"icon-xs": inside a 1px-bordered group the
    // field lands on the same 28px/24px chip heights, including the desktop
    // 12px text downshift.
    size === "xs" &&
      "h-6.5 px-[calc(--spacing(2)-1px)] text-sm leading-6.5 sm:h-5.5 sm:text-xs sm:leading-5.5",
    size === "sm" &&
      "h-7.5 px-[calc(--spacing(2.5)-1px)] leading-7.5 sm:h-6.5 sm:leading-6.5",
    size === "lg" && "h-9.5 leading-9.5 sm:h-8.5 sm:leading-8.5",
    // Pairs with Button size="xl": the same 44px/40px heights.
    size === "xl" &&
      "h-11 px-[calc(--spacing(3.5)-1px)] leading-11 sm:h-10 sm:leading-10",
    // Matches the outer height of a dock with icon-xl actions after the
    // dock's 4px padding and both 1px borders are included.
    size === "2xl" &&
      "h-13 px-[calc(--spacing(4)-1px)] leading-13 sm:h-12 sm:leading-12",
    props.type === "search" &&
      "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
    // The UA spinner is unstyleable by tokens; keep ↑/↓ stepping, drop the buttons (NumberField composes −/+).
    props.type === "number" &&
      "tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
    props.type === "file" &&
      "text-muted-foreground file:me-3 file:bg-transparent file:font-medium file:text-foreground file:text-sm",
  );

  return (
    <span
      className={
        cn(
          !unstyled &&
            "relative inline-flex w-full select-text rounded-lg border border-input bg-background not-dark:bg-clip-padding text-base text-foreground ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-aria-invalid:text-destructive-foreground has-focus-visible:border-ring has-autofill:bg-foreground/4 has-disabled:cursor-not-allowed has-disabled:opacity-64 has-focus-visible:ring-[3px] sm:text-sm dark:has-autofill:bg-foreground/8 dark:has-focus-visible:has-aria-invalid:ring-destructive/24",
          !unstyled &&
            (variant === "outline"
              ? "transition-[box-shadow,border-color] not-has-disabled:not-has-focus-visible:hover:border-muted-foreground/60"
              : variant === "ghost"
                ? "bg-transparent dark:bg-transparent"
                : variant === "table"
                  ? TABLE_CONTROL_CLASS
                  : "shadow-xs/5 not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none dark:bg-input/32 dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]"),
          // A raw-canvas control floats: the popover surface shadow, shared
          // with every `ControlBar backgroundColor` bar over the same canvas.
          !unstyled && backgroundColor != null && "shadow-popover",
          className,
        ) || undefined
      }
      data-size={size}
      data-slot="input-control"
      style={
        backgroundColor == null && borderColor == null
          ? undefined
          : {
              ...(backgroundColor != null && { backgroundColor }),
              // The resting border reads `--input`; scoping the variable
              // recolors it while the focus/hover/invalid classes (which set
              // border-color outright) keep winning over it.
              ...(borderColor != null && { "--input": borderColor }),
            }
      }
    >
      {nativeInput ? (
        <input
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          style={typeof style === "function" ? undefined : style}
          {...props}
        />
      ) : (
        <InputPrimitive
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          style={style}
          {...props}
        />
      )}
    </span>
  );
}
